import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack';
import type { FetchOptions } from 'ofetch';

type FetchMethod = 'options' | 'get' | 'put' | 'post' | 'patch' | 'delete' | 'head' | 'options' | 'trace' | 'connect';
type VersionedFetchFunction<M extends FetchMethod> = <T = unknown>(url: string, options?: NitroFetchOptions<NitroFetchRequest, M> & { version?: string }) => Promise<T>;

/**
 * Returns a map of $fetch instances, where the name of the instance is
 * the HTTP method. Each instance inherits from a base instance that handles
 * setting authentication headers, caching, and logging.
 */
export default () => {
  const { serverBaseUrl } = useRuntimeConfig().public;
  const DEBUG = isDevelop();

  const api = $fetch.create({
    onResponse({ request, response, options }) {
      // If we're not in production mode, log this request
      if (DEBUG) {
        const statusCode = response.status;
        const method = (options.method as string);
        console.log(`[API] [${method}](${statusCode}) ${request}`);
      }
    },
  });

  const getAuthHeader = async () => {
    // Get the user's server token and add it as an Authorization header
    const { getSession } = useAuth();
    const sessionData = await getSession({ required: true });
    return { Authorization: `Bearer ${sessionData?.server_token || '_'}` };
  };

  const requestWithAuthHeader = <F extends (...args: any[]) => any>(func: F, options: NitroFetchOptions<NitroFetchRequest>) => {
    return (async (url: string, innerOptions: FetchOptions & { version?: string } = {}) => {
      const authHeader = await getAuthHeader();
      const finalOptions = {
        baseURL: formatApiEndpoint(serverBaseUrl, '', innerOptions.version),
        ...options,
        ...innerOptions,
      };
      finalOptions.headers = {
        ...(finalOptions.headers || {}),
        ...authHeader,
      };
      return await func(url, finalOptions) as F;
    }) as F;
  };

  return {
    get: requestWithAuthHeader<VersionedFetchFunction<'get'>>(api, { method: 'GET' }),
    post: requestWithAuthHeader<VersionedFetchFunction<'post'>>(api, { method: 'POST' }),
    put: requestWithAuthHeader<VersionedFetchFunction<'put'>>(api, { method: 'PUT' }),
    patch: requestWithAuthHeader<VersionedFetchFunction<'patch'>>(api, { method: 'PATCH' }),
    delete: requestWithAuthHeader<VersionedFetchFunction<'delete'>>(api, { method: 'DELETE' }),
    options: requestWithAuthHeader<VersionedFetchFunction<'options'>>(api, { method: 'OPTIONS' }),
  };
};
